import { useBuy } from "@/context/buy";
import { encryptInteger } from "@/Utils/encryptInteger";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

export default function OTPModal() {
  const router = useRouter();
  const {
    setOpenModal,
    createUserDetails,
    setLoggedUserDetails,
    openSingleModal,
    isSocial,
    googleToken,
  } = useBuy();

  const inputRefs = useRef([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    inputRefs.current = Array(6).fill(null);
  }, []);

  const getOtp = () => inputRefs.current.map((input) => input.value).join("");

  const handleInputChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value) {
      inputRefs.current[index].value = value;
      if (index < 5) inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      inputRefs.current[index].value = "";
      if (index > 0) inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    
    paste.split("").forEach((digit, i) => {
      if (inputRefs.current[i]) {
        inputRefs.current[i].value = digit;
      }
    });

    const nextEmptyIndex = paste.length < 6 ? paste.length : 5;
    if (inputRefs.current[nextEmptyIndex]) {
      inputRefs.current[nextEmptyIndex].focus();
    }
  };

  const handleAutofill = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    if (value.length === 6) {
      value.split("").forEach((digit, i) => {
        if (inputRefs.current[i]) inputRefs.current[i].value = digit;
      });
      inputRefs.current[5].focus();
    }
  };

  const createAcc = async () => {
    const otpValue = getOtp();
    if (otpValue.length < 6)
      return toast.error("Please enter the complete OTP");
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const confirmationResult = createUserDetails.confirmationResult;
      const res = await confirmationResult.confirm(otpValue);
      const user = res.user;

      const formData = new FormData();
      const updatePhoneNumber = createUserDetails.phone_number.replace(
        createUserDetails.country_code,
        ""
      );

      formData.append("agree", createUserDetails.agree);
      formData.append("is_sms_on", createUserDetails.is_sms_on ? 1 : 0);
      formData.append("city", createUserDetails.city);
      if (!isSocial) {
        formData.append("email", createUserDetails.email);
        formData.append("password", createUserDetails.password);
        formData.append("action", "createAccount");
      } else {
        formData.append("action", "addProfile");
        formData.append("token", googleToken);
      }
      formData.append("name", createUserDetails.name);
      formData.append("phone_number", `+${updatePhoneNumber}`);
      formData.append("zipcode", createUserDetails.zipcode);
      formData.append("state", createUserDetails.state);
      formData.append("country_code", createUserDetails.country_code);

      const headers = isSocial
        ? { Authorization: `Bearer ${googleToken}` }
        : undefined;
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`,
        {
          method: "POST",
          body: formData,
          headers,
        }
      );

      const data = await response.json();

      if (data.status) {
        toast.success(data.message);
        const storingData = {
          profile: data?.profile,
          web_token_expires_at:
            data?.web_token_expires_at ?? data.data?.web_token_expires_at,
          web_token: data?.web_token ?? data?.data?.web_token,
          name: createUserDetails?.name,
          state: createUserDetails?.state,
          user_type: data?.data?.user_type,
        };
        const encryptedData = encryptInteger(JSON.stringify(storingData));
        localStorage.setItem("user_data", encryptedData);
        setLoggedUserDetails({
          status: true,
          data: storingData,
          remove: false,
        });
        setOpenModal((prev) => ({ ...prev, otp: false }));
        let back_url = sessionStorage.getItem("last_url");
        router.push(back_url ?? "/");
        openSingleModal("buy");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Invalid OTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="LoginModal" className="bg-gray-800 relative z-[9999999999999]">
      <div className="flex flex-col md:flex-row justify-start lg:items-center lg:justify-center fixed overflow-y-auto h-full inset-0 px-3 md:px-0 bg-gray-700/50 z-[999]">
        <div className="md:hidden" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-end mt-3 -mr-7">
            <div
              onClick={() => setOpenModal((prev) => ({ ...prev, otp: false }))}
              className="w-10 h-10 rounded-full bg-[#1F222A] flex items-center justify-center mb-2"
            >
              <svg
                width="13"
                height="12"
                viewBox="0 0 13 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.144 12L5.496 5.352V6.552L0.456 0.287999H3.288L6.864 4.848H6L9.576 0.287999H12.408L7.368 6.504V5.424L12.72 12H9.84L6 7.152H6.816L3 12H0.144Z"
                  fill="#6A798F"
                />
              </svg>
            </div>
          </div>
        </div>
        <div
          className="bg-[#17191d] h-fit w-full max-w-[620px] overflow-hidden md:my-[20px] overflow-y-auto px-4 lg:px-[40px] pb-[40px] rounded-[16px] lg:rounded-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mt-6 lg:mt-0 lg:px-4">
            <div className="lg:mt-11">
              <p className="py-3 pl-2 text-base lg:text-xl max-w-[600px] mx-auto">
                Verify Phone Number
              </p>
            </div>
            <form className="mt-5 pl-2 lg:pl-0 lg:mt-7">
              <div className="lg:mt-6 mb-[80px] lg:mb-[150px] max-w-[600px] mx-auto">
                <p className="text-start">
                  Enter the code we sent to your phone
                </p>
                <div
                  onPaste={handlePaste}
                  className="flex justify-center gap-4 mt-4"
                >
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="one-time-code"
                    onChange={handleAutofill}
                    className="absolute opacity-0 w-0 h-0"
                    aria-hidden="true"
                  />
                  {[0, 1, 2, 3, 4, 5].map((_, index) => (
                    <input
                      key={index}
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      autoComplete="off"
                      ref={(el) => (inputRefs.current[index] = el)}
                      className="text-[20px] bg-transparent p-4 border border-[#35383F] rounded-[16px] focus:outline-[#FA2CB4] focus:bg-[#E74CF414] text-center w-[40px] h-[50px] lg:w-[70px] lg:h-[60px] password-dot"
                      onChange={(e) => handleInputChange(e, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                    />
                  ))}
                </div>
              </div>
            </form>
          </div>
          <div className="mt-5 lg:mt-12 pt-1 relative">
            <div className="absolute w-[120%] left-[-10%]">
              <svg
                width="100%"
                height="2"
                viewBox="0 0 694 2"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line y1="1" x2="694.001" y2="1" stroke="#332D41" />
              </svg>
            </div>
          </div>
          <div className="mt-5 lg:mt-8 modal-footer flex flex-col lg:flex-row gap-4 lg:gap-8 items-center justify-end">
            <button
              onClick={createAcc}
              className="main_btn_bg font-urbanist lg:order-2 font-[800] text-[14px] lg:text-base py-[6px] lg:py-5 px-[62px] w-[179px] lg:w-[340px] rounded-[10px] lg:leading-[10px]"
            >
              {isSubmitting ? "Verifying..." : "Continue"}
            </button>
            <button
              onClick={() => setOpenModal((prev) => ({ ...prev, otp: false }))}
              className="border-[#35383F] hidden lg:block bg-[#6b6b6b17] font-urbanist font-[500] border text-[14px] lg:text-base py-[6px] lg:py-[19px] lg:leading-[10px] px-[62px] w-[179px] lg:w-[340px] rounded-[10px]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
